import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicPlans, type PublicPlan } from '@data/publicPlans';
import { getOffers, type Offer } from '@data/offers';
import { projects, getProjectDisplayName, type Project } from '@data/projects';
import AdminAnalyticsNav from '@components/admin/AdminAnalyticsNav';
import { getPlans, type Plan } from '@services/plans';
import { getAdminActivityOverview, type ActivityOverviewResponse } from '@services/adminActivity';
import { hasAdminCapability, useAuth } from '@services/auth';
import { useSearchHighlight } from '../../shared/useSearchHighlight';

const currency = new Intl.NumberFormat('en-DE', {
	style: 'currency',
	currency: 'EUR',
	maximumFractionDigits: 0
});

const percent = (value: number) => {
	const clamped = Math.min(Math.max(value, 0), 1);
	return `${Math.round(clamped * 100)}%`;
};

const satisfactionSignals = [
	{ label: 'Post-project CSAT', value: 4.3, delta: '+0.2', context: 'target ≥ 4.0' },
	{ label: 'Gardener response SLA', value: '89%', delta: '-3%', context: 'goal ≥ 95%' },
	{ label: 'Dispute resolution time', value: '2.1 days', delta: '-0.6', context: 'goal ≤ 3 days' }
];

const aiPipelineSignals = [
	{ label: 'AI plans generated (24h)', value: 18, delta: '+38%' },
	{ label: 'Manual edits required', value: '42%', delta: '-6%' },
	{ label: 'Average turnaround', value: '12m', delta: '-2m' }
];

export default function AdminAnalytics() {
	const { user } = useAuth();
	const canViewErrorDetails = hasAdminCapability(user, 'admin_errors');
	const [plans, setPlans] = useState<PublicPlan[]>(() => getPublicPlans());
	const [allOffers, setAllOffers] = useState<Offer[]>(() => getOffers({ includeDisabled: true }));
	const [dbPlans, setDbPlans] = useState<Plan[]>([]);
	const [activityOverview, setActivityOverview] = useState<ActivityOverviewResponse | null>(null);
	const [activityDays, setActivityDays] = useState(14);
	useSearchHighlight(); // Handle search highlighting

	useEffect(() => {
		const handlePlansUpdate = () => setPlans(getPublicPlans());
		const handleOffersUpdate = () => setAllOffers(getOffers({ includeDisabled: true }));

		window.addEventListener('publicPlansUpdated', handlePlansUpdate);
		window.addEventListener('offersUpdated', handleOffersUpdate);

		setPlans(getPublicPlans());
		setAllOffers(getOffers({ includeDisabled: true }));
		getPlans(undefined, { includeHidden: true, includeDeleted: true })
			.then(setDbPlans)
			.catch((err) => {
				console.warn('Failed to load plans for analytics', err);
			});
		getAdminActivityOverview({ days: activityDays })
			.then(setActivityOverview)
			.catch((err) => {
				console.warn('Failed to load admin activity overview', err);
			});

		return () => {
			window.removeEventListener('publicPlansUpdated', handlePlansUpdate);
			window.removeEventListener('offersUpdated', handleOffersUpdate);
		};
	}, [activityDays]);

	const planStats = useMemo(() => {
		const statusCounts = plans.reduce(
			(acc, plan) => {
				acc.total += 1;
				acc.status[plan.status] = (acc.status[plan.status] ?? 0) + 1;
				if (plan.moderationStatus === 'hidden') acc.hidden += 1;
				if (plan.moderationStatus !== 'hidden') acc.visible += 1;
				acc.seekers.add(plan.seekerUserId);
				return acc;
			},
			{
				total: 0,
				visible: 0,
				hidden: 0,
				status: {} as Record<PublicPlan['status'], number>,
				seekers: new Set<string>()
			}
		);

		return {
			total: statusCounts.total,
			visible: statusCounts.visible,
			hidden: statusCounts.hidden,
			seekers: statusCounts.seekers.size,
			status: {
				draft: statusCounts.status.draft ?? 0,
				active: statusCounts.status.active ?? 0,
				closed: statusCounts.status.closed ?? 0
			}
		};
	}, [plans]);

	const offerStats = useMemo(() => {
		const moderationCounts = { active: 0, disabled: 0 };
		const statusCounts: Record<Offer['status'], number> = { pending: 0, accepted: 0, rejected: 0 };
		let midBandsTotal = 0;

		for (const offer of allOffers) {
			if (offer.moderationStatus === 'disabled') moderationCounts.disabled += 1;
			else moderationCounts.active += 1;

			statusCounts[offer.status] = (statusCounts[offer.status] ?? 0) + 1;
			midBandsTotal += offer.bands.mid;
		}

		const total = allOffers.length;
		const acceptanceRate = total === 0 ? 0 : statusCounts.accepted / total;
		const avgOffer = total === 0 ? 0 : midBandsTotal / total;

		return {
			total,
			moderationCounts,
			statusCounts,
			acceptanceRate,
			avgOffer
		};
	}, [allOffers]);

	const pipelineStats = useMemo(() => {
		const statusCounts = projects.reduce(
			(acc, project) => {
				const status = project.status;
				if (status === 'planning' || status === 'quoted' || status === 'in_progress' || status === 'completed' || status === 'cancelled') {
					acc[status] = (acc[status] ?? 0) + 1;
				}
				acc.budgetTotal += project.budget;
				if (project.status !== 'completed') {
					acc.openBudget += project.budget;
				}
				return acc;
			},
			{
				budgetTotal: 0,
				openBudget: 0,
				planning: 0,
				quoted: 0,
				in_progress: 0,
				completed: 0,
				cancelled: 0
			} as {
				budgetTotal: number;
				openBudget: number;
				planning: number;
				quoted: number;
				in_progress: number;
				completed: number;
				cancelled: number;
			}
		);

		const upcoming: Project[] = projects
			.filter(p => p.dueDate)
			.sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
			.slice(0, 3);

		return { statusCounts, upcoming };
	}, []);

	const supplyDemandRatio =
		planStats.visible === 0 ? 0 : offerStats.moderationCounts.active / planStats.visible;

	const getGenerationDurationSeconds = (plan: Plan): number | null => {
		const raw = plan.generation_duration_seconds;
		if (raw === null || raw === undefined) return null;
		const parsed = typeof raw === 'number' ? raw : Number(raw);
		if (!Number.isFinite(parsed) || parsed < 0) return null;
		return Math.floor(parsed);
	};

	const generationTimeStats = useMemo(() => {
		const measured = dbPlans
			.map((p) => getGenerationDurationSeconds(p))
			.filter((seconds): seconds is number => seconds !== null)
			.sort((a, b) => a - b);

		if (measured.length === 0) {
			return {
				count: 0,
				average: null as number | null,
				median: null as number | null,
				max: null as number | null
			};
		}

		const total = measured.reduce((sum, val) => sum + val, 0);
		const middle = Math.floor(measured.length / 2);
		const median =
			measured.length % 2 === 0
				? Math.round((measured[middle - 1] + measured[middle]) / 2)
				: measured[middle];

		return {
			count: measured.length,
			average: Math.round(total / measured.length),
			median,
			max: measured[measured.length - 1]
		};
	}, [dbPlans]);

	const recentGenerationTimings = useMemo(
		() =>
			dbPlans
				.filter((p) => getGenerationDurationSeconds(p) !== null)
				.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
				.slice(0, 8),
		[dbPlans]
	);

	const formatDuration = (seconds?: number | null) => {
		if (typeof seconds !== 'number' || seconds < 0) return '—';
		const minutes = Math.floor(seconds / 60);
		const remaining = seconds % 60;
		return `${minutes}:${String(remaining).padStart(2, '0')}`;
	};

	const marketplaceHighlights = [
		{
			label: 'Plans funnel',
			value: `${planStats.status.active} active / ${planStats.status.draft} drafts`,
			context: `${planStats.hidden} hidden`,
			icon: 'bi-diagram-3'
		},
		{
			label: 'Average accepted quote',
			value: currency.format(Math.round(offerStats.avgOffer || 0)),
			context: `${percent(offerStats.acceptanceRate)} acceptance`,
			icon: 'bi-currency-euro'
		},
		{
			label: 'Supply vs demand',
			value: supplyDemandRatio.toFixed(1),
			context: 'offers per visible plan',
			icon: 'bi-people-fill'
		},
		{
			label: 'Pipeline value',
			value: currency.format(pipelineStats.statusCounts ? pipelineStats.statusCounts.openBudget : 0),
			context: `${pipelineStats.statusCounts?.completed ?? 0} completed projects`,
			icon: 'bi-briefcase'
		}
	];
	const activityMetrics = activityOverview?.metrics;
	const topEvents = activityOverview?.event_breakdown?.slice(0, 12) ?? [];
	const topPages = activityOverview?.page_breakdown?.slice(0, 12) ?? [];
	const topCtas = activityOverview?.top_ctas ?? [];
	const funnel = activityOverview?.funnel ?? [];
	const deviceBreakdown = activityOverview?.device_breakdown ?? [];
	const browserBreakdown = activityOverview?.browser_breakdown ?? [];
	const countryBreakdown = activityOverview?.country_breakdown ?? [];
	const recentEvents = activityOverview?.recent_events?.slice(0, 20) ?? [];
	const questionnaireSteps = activityOverview?.questionnaire_steps ?? [];
	const questionnaireExits = activityOverview?.questionnaire_exits ?? [];
	const frictionSignals = activityOverview?.friction_signals;
	const exitsByStep = useMemo(
		() =>
			questionnaireExits.reduce<Record<number, number>>((acc, row) => {
				acc[Number(row.step_number)] = Number(row.exits || 0);
				return acc;
			}, {}),
		[questionnaireExits]
	);
	const dropoffRows = useMemo(
		() =>
			questionnaireSteps.map((row) => {
				const stepNum = Number(row.step_number);
				const views = Number(row.views || 0);
				const nextClicks = Number(row.next_clicks || 0);
				const exits = Number(exitsByStep[stepNum] || 0);
				const validationErrors = Number(row.validation_errors || 0);
				const proceedRate = views > 0 ? nextClicks / views : 0;
				const dropoffRate = views > 0 ? (views - nextClicks) / views : 0;
				return {
					stepNum,
					views,
					nextClicks,
					exits,
					validationErrors,
					proceedRate,
					dropoffRate,
					avgExitSeconds: row.avg_exit_seconds ?? null
				};
			}),
		[questionnaireSteps, exitsByStep]
	);
	const topDropoff = useMemo(() => {
		if (!dropoffRows.length) return null;
		return [...dropoffRows].sort((a, b) => b.dropoffRate - a.dropoffRate)[0];
	}, [dropoffRows]);
	const questionnaireCompletionRate =
		(activityMetrics?.questionnaire_starts ?? 0) > 0
			? (activityMetrics?.questionnaire_submits ?? 0) / (activityMetrics?.questionnaire_starts ?? 0)
			: 0;
	const loginSuccessRate =
		(activityMetrics?.login_clicks ?? 0) > 0
			? (activityMetrics?.login_successes ?? 0) / (activityMetrics?.login_clicks ?? 0)
			: 0;

	return (
		<>
			<div className="d-flex justify-content-between align-items-start mb-4">
				<div>
					<h1 className="h3 mb-1">Admin Insights</h1>
					<p className="text-muted small mb-0">
						Analytics, trends & performance metrics · <Link to="/app/dashboard" className="text-decoration-none" style={{ color: '#2f9e59' }}>← Operational dashboard</Link>
					</p>
					<p className="text-muted mb-0 mt-2">
						Deep dive into marketplace health, operational performance, and AI-assisted workflows.{' '}
						<Link to="/app/admin/user-activity" className="text-decoration-none" style={{ color: '#2f9e59' }}>
							Per-user activity →
						</Link>
					</p>
				</div>
				<div className="d-flex align-items-center gap-2">
					<label className="text-muted small mb-0">Activity window</label>
					<select
						className="form-select form-select-sm"
						value={activityDays}
						onChange={(e) => setActivityDays(Number(e.target.value))}
						style={{ width: 110 }}
					>
						<option value={7}>7 days</option>
						<option value={14}>14 days</option>
						<option value={30}>30 days</option>
						<option value={90}>90 days</option>
					</select>
				</div>
			</div>

			<AdminAnalyticsNav />

			<div className="row g-3 mb-3">
				<div className="col-12 col-xl-3">
					<div className="card h-100 border-top border-4 border-danger-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Priority: questionnaire completion</div>
							<div className="display-6 my-1">{percent(questionnaireCompletionRate)}</div>
							<div className="text-muted small">
								{activityMetrics?.questionnaire_submits ?? 0} of {activityMetrics?.questionnaire_starts ?? 0} starts
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-3">
					<div className="card h-100 border-top border-4 border-danger-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Priority: top drop-off step</div>
							<div className="display-6 my-1">
								{topDropoff ? `Step ${topDropoff.stepNum}` : '—'}
							</div>
							<div className="text-muted small">
								{topDropoff ? `${percent(topDropoff.dropoffRate)} drop-off` : 'No step data yet'}
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-3">
					<div className="card h-100 border-top border-4 border-danger-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Priority: login success rate</div>
							<div className="display-6 my-1">{percent(loginSuccessRate)}</div>
							<div className="text-muted small">
								{activityMetrics?.login_successes ?? 0} success from {activityMetrics?.login_clicks ?? 0} clicks
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-3">
					<div className="card h-100 border-top border-4 border-danger-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Priority: generation success rate</div>
							<div className="display-6 my-1">
								{activityMetrics?.plan_generation_starts
									? percent((activityMetrics.plan_generation_successes ?? 0) / activityMetrics.plan_generation_starts)
									: '0%'}
							</div>
							<div className="text-muted small">
								{activityMetrics?.plan_generation_successes ?? 0} success · {activityMetrics?.plan_generation_errors ?? 0} errors
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-success-subtle">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center">
								<span className="text-muted small text-uppercase">Unique visitors (14d)</span>
								<i className="bi bi-person-check text-success"></i>
							</div>
							<div className="display-6 my-1">{activityMetrics?.unique_visitors ?? '—'}</div>
							<div className="text-muted small">{activityMetrics?.seeker_home_views ?? 0} seeker homepage views</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-primary-subtle">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center">
								<span className="text-muted small text-uppercase">Avg engaged time</span>
								<i className="bi bi-grid text-primary"></i>
							</div>
							<div className="display-6 my-1">{Math.round(activityMetrics?.avg_engaged_seconds ?? 0)}s</div>
							<div className="text-muted small">heartbeat-based active engagement</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-warning-subtle">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center">
								<span className="text-muted small text-uppercase">Questionnaire funnel</span>
								<i className="bi bi-envelope-open text-warning"></i>
							</div>
							<div className="display-6 my-1">{activityMetrics?.questionnaire_starts ?? 0}</div>
							<div className="text-muted small">{activityMetrics?.questionnaire_submits ?? 0} completed</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-info-subtle">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center">
								<span className="text-muted small text-uppercase">Plan generation success</span>
								<i className="bi bi-graph-up-arrow text-info"></i>
							</div>
							<div className="display-6 my-1">
								{activityMetrics?.plan_generation_starts
									? percent((activityMetrics.plan_generation_successes ?? 0) / activityMetrics.plan_generation_starts)
									: '0%'}
							</div>
							<div className="text-muted small">
								{activityMetrics?.plan_generation_successes ?? 0} success · {activityMetrics?.plan_generation_errors ?? 0} errors
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-secondary-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Tracked events</div>
							<div className="fs-3 fw-semibold my-1">{activityMetrics?.total_events ?? 0}</div>
							<div className="text-muted small">{activityMetrics?.total_sessions ?? 0} sessions</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-secondary-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Known users</div>
							<div className="fs-3 fw-semibold my-1">{activityMetrics?.total_known_users ?? 0}</div>
							<div className="text-muted small">{activityMetrics?.total_anonymous_ids ?? 0} anonymous ids</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-secondary-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Avg session duration</div>
							<div className="fs-3 fw-semibold my-1">{Math.round(activityMetrics?.avg_session_seconds ?? 0)}s</div>
							<div className="text-muted small">from tracked page exit events</div>
						</div>
					</div>
				</div>
				<div className="col-sm-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-secondary-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Auth split</div>
							<div className="fs-3 fw-semibold my-1">
								{activityMetrics?.logged_in_events ?? 0} / {activityMetrics?.anonymous_events ?? 0}
							</div>
							<div className="text-muted small">logged-in vs anonymous events</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3">
				<div className="col-12 col-xl-6">
					<div className="card h-100" data-section-id="marketplace-health">
						<div className="card-header d-flex justify-content-between align-items-center">
							<span>Marketplace health analysis</span>
							<span className="badge text-bg-light">{offerStats.total} offers / {planStats.visible} visible plans</span>
						</div>
						<div className="card-body">
							<div className="row g-3">
								{marketplaceHighlights.map(item => {
									// Map labels to section IDs
									const sectionIdMap: Record<string, string> = {
										'Supply vs demand': 'supply-demand',
										'Plans funnel': 'plans-funnel',
										'Average accepted quote': 'average-quote',
										'Pipeline value': 'pipeline-value'
									};
									const sectionId = sectionIdMap[item.label];
									return (
										<div key={item.label} className="col-12 col-md-6">
											<div 
												className="border rounded p-3 h-100"
												{...(sectionId ? { 'data-section-id': sectionId } as React.HTMLAttributes<HTMLDivElement> : {})}
											>
												<div className="d-flex align-items-center gap-2">
													<i className={`bi ${item.icon} text-secondary`}></i>
													<span className="text-muted small text-uppercase">{item.label}</span>
												</div>
												<div className="fs-4 fw-semibold my-1">{item.value}</div>
												<div className="text-muted small">{item.context}</div>
											</div>
										</div>
									);
								})}
							</div>
							<hr />
							<div className="row text-muted small text-uppercase gx-0">
								<div className="col">Offers</div>
								<div className="col text-end">Accepted</div>
								<div className="col text-end">Rejected</div>
								<div className="col text-end">Pending</div>
							</div>
							<div className="row align-items-center gx-0">
								<div className="col">
									<div className="progress" style={{ height: '0.4rem' }}>
										<div
											className="progress-bar bg-success"
											style={{
												width: percent(offerStats.total === 0 ? 0 : offerStats.statusCounts.accepted / offerStats.total)
											}}
										/>
										<div
											className="progress-bar bg-danger"
											style={{
												width: percent(offerStats.total === 0 ? 0 : offerStats.statusCounts.rejected / offerStats.total)
											}}
										/>
										<div
											className="progress-bar bg-warning"
											style={{
												width: percent(offerStats.total === 0 ? 0 : offerStats.statusCounts.pending / offerStats.total)
											}}
										/>
									</div>
								</div>
								<div className="col text-end small">{offerStats.statusCounts.accepted}</div>
								<div className="col text-end small">{offerStats.statusCounts.rejected}</div>
								<div className="col text-end small">{offerStats.statusCounts.pending}</div>
							</div>
						</div>
					</div>
				</div>

				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header d-flex justify-content-between align-items-center">
							<span>Performance signals</span>
							<span className="badge text-bg-info-subtle text-info-emphasis">Analytics</span>
						</div>
						<div className="card-body">
							<div className="fw-semibold text-muted small text-uppercase mb-2">Upcoming project checkpoints</div>
							<ul className="list-group list-group-flush mb-3">
								{pipelineStats.upcoming.map(project => (
									<li key={project.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
										<div>
											<div className="fw-medium">{getProjectDisplayName(project)}</div>
											<div className="text-muted small">
												{project.status.replace('_', ' ')} • {currency.format(project.budget)}
											</div>
										</div>
										<span className="badge text-bg-light">{project.dueDate ? new Date(project.dueDate).toLocaleDateString('en-GB') : 'TBC'}</span>
									</li>
								))}
								{pipelineStats.upcoming.length === 0 && (
									<li className="list-group-item px-0 text-muted small">No upcoming milestones scheduled.</li>
								)}
							</ul>

							<div className="fw-semibold text-muted small text-uppercase mb-2">Service signals</div>
							<div className="row g-2">
								{satisfactionSignals.map(signal => (
									<div key={signal.label} className="col-12 col-md-4">
										<div className="border rounded p-3 h-100 text-center">
											<div className="text-muted small">{signal.label}</div>
											<div className="fs-5 fw-semibold my-1">{signal.value}</div>
											<div className="text-success small">{signal.delta}</div>
											<div className="text-muted small">{signal.context}</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12">
					<div className="card">
						<div className="card-header">Questionnaire step drop-off & timing</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Step</th>
											<th className="text-end">Views</th>
											<th className="text-end">Next clicks</th>
											<th className="text-end">Proceed rate</th>
											<th className="text-end">Drop-off rate</th>
											<th className="text-end">Exits</th>
											<th className="text-end">Validation errors</th>
											<th className="text-end">Avg exit sec</th>
										</tr>
									</thead>
									<tbody>
										{dropoffRows.map((row) => (
											<tr key={row.stepNum}>
												<td>Step {row.stepNum}</td>
												<td className="text-end">{row.views}</td>
												<td className="text-end">{row.nextClicks}</td>
												<td className="text-end">{percent(row.proceedRate)}</td>
												<td className="text-end">{percent(row.dropoffRate)}</td>
												<td className="text-end">{row.exits}</td>
												<td className="text-end">{row.validationErrors}</td>
												<td className="text-end">{row.avgExitSeconds ? Math.round(row.avgExitSeconds) : 0}</td>
											</tr>
										))}
										{dropoffRows.length === 0 && (
											<tr>
												<td colSpan={8} className="text-muted small">No questionnaire step data yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">Weekly activity</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Date</th>
											<th className="text-end">Homepage views</th>
											<th className="text-end">Unique visitors</th>
										</tr>
									</thead>
									<tbody>
										{(activityOverview?.daily ?? []).map((row) => (
											<tr key={row.day}>
												<td>{new Date(row.day).toLocaleDateString('en-GB')}</td>
												<td className="text-end">{row.views}</td>
												<td className="text-end">{row.unique_visitors}</td>
											</tr>
										))}
										{!(activityOverview?.daily?.length) && (
											<tr>
												<td colSpan={3} className="text-muted small">No activity events yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>

				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">AI assistance signals</div>
						<div className="card-body">
							<ul className="list-group list-group-flush">
								{aiPipelineSignals.map(signal => (
									<li key={signal.label} className="list-group-item px-0 d-flex justify-content-between align-items-center">
										<div>
											<div className="fw-medium">{signal.label}</div>
											<div className="text-muted small">Rolling 24h window</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold">{signal.value}</div>
											<div className="text-success small">{signal.delta}</div>
										</div>
									</li>
								))}
							</ul>
							<hr />
							<div className="fw-semibold text-muted small text-uppercase mb-2">Generation timing (database)</div>
							<div className="row g-2 mb-3">
								<div className="col-4">
									<div className="border rounded p-2 text-center">
										<div className="text-muted small">Avg</div>
										<div className="fw-semibold">{formatDuration(generationTimeStats.average)}</div>
									</div>
								</div>
								<div className="col-4">
									<div className="border rounded p-2 text-center">
										<div className="text-muted small">Median</div>
										<div className="fw-semibold">{formatDuration(generationTimeStats.median)}</div>
									</div>
								</div>
								<div className="col-4">
									<div className="border rounded p-2 text-center">
										<div className="text-muted small">Slowest</div>
										<div className="fw-semibold">{formatDuration(generationTimeStats.max)}</div>
									</div>
								</div>
							</div>
							<div className="small text-muted mb-2">
								Measured plans: {generationTimeStats.count}
							</div>
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Plan</th>
											<th className="text-end">Duration</th>
										</tr>
									</thead>
									<tbody>
										{recentGenerationTimings.length === 0 && (
											<tr>
												<td colSpan={2} className="text-muted small">No saved generation timings yet.</td>
											</tr>
										)}
										{recentGenerationTimings.map((plan) => (
											<tr key={plan.id}>
												<td>
													<div className="fw-medium">{plan.title}</div>
													<div className="text-muted small">{new Date(plan.created_at).toLocaleString('en-GB')}</div>
												</td>
												<td className="text-end fw-semibold">
													{formatDuration(getGenerationDurationSeconds(plan))}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">Behavior funnel</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Stage</th>
											<th className="text-end">Count</th>
											<th className="text-end">Conversion vs start</th>
										</tr>
									</thead>
									<tbody>
										{funnel.map((step, idx) => {
											const start = funnel[0]?.count || 0;
											const ratio = start > 0 ? step.count / start : 0;
											return (
												<tr key={step.key}>
													<td>{idx + 1}. {step.label}</td>
													<td className="text-end fw-semibold">{step.count}</td>
													<td className="text-end">{percent(ratio)}</td>
												</tr>
											);
										})}
										{funnel.length === 0 && (
											<tr>
												<td colSpan={3} className="text-muted small">No funnel events yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">Top CTA clicks</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>CTA</th>
											<th className="text-end">Clicks</th>
										</tr>
									</thead>
									<tbody>
										{topCtas.map((row) => (
											<tr key={row.cta_name ?? 'unknown'}>
												<td>{row.cta_name || '(unknown)'}</td>
												<td className="text-end fw-semibold">{row.clicks}</td>
											</tr>
										))}
										{topCtas.length === 0 && (
											<tr>
												<td colSpan={2} className="text-muted small">No CTA clicks tracked yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">Top pages</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Page</th>
											<th className="text-end">Views</th>
											<th className="text-end">Unique visitors</th>
											<th className="text-end">Sessions</th>
										</tr>
									</thead>
									<tbody>
										{topPages.map((row) => (
											<tr key={row.page_key}>
												<td className="text-break">{row.page_key}</td>
												<td className="text-end">{row.views}</td>
												<td className="text-end">{row.unique_visitors}</td>
												<td className="text-end">{row.sessions}</td>
											</tr>
										))}
										{topPages.length === 0 && (
											<tr>
												<td colSpan={4} className="text-muted small">No page activity available yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header">Top events</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Event</th>
											<th className="text-end">Count</th>
											<th className="text-end">Sessions</th>
										</tr>
									</thead>
									<tbody>
										{topEvents.map((row) => (
											<tr key={row.event_name}>
												<td>{row.event_name}</td>
												<td className="text-end">{row.events}</td>
												<td className="text-end">{row.sessions}</td>
											</tr>
										))}
										{topEvents.length === 0 && (
											<tr>
												<td colSpan={3} className="text-muted small">No event stats available yet.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12">
					<div className="card">
						<div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
							<span>Friction signals (what is blocking users)</span>
							{canViewErrorDetails && (
								<Link to="/app/admin/errors" className="small text-decoration-none" style={{ color: '#2f9e59' }}>
									View error details →
								</Link>
							)}
						</div>
						<div className="card-body">
							<div className="row g-3">
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Validation errors</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.questionnaire_validation_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Upload errors</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.questionnaire_upload_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">AI analysis errors</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.questionnaire_ai_analysis_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Generation errors</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.plan_generation_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Missing questionnaire</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.plan_flow_missing_questionnaire ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Handoff storage failed</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.plan_flow_handoff_storage_failed ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Login failures</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.login_failures ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-4 col-xl-2">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Signup failures</div>
										<div className="fs-4 fw-semibold">{frictionSignals?.signup_failures ?? 0}</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mt-1">
				<div className="col-12 col-md-6 col-xl-3">
					<div className="card h-100">
						<div className="card-header">Device distribution</div>
						<div className="card-body">
							<ul className="list-group list-group-flush">
								{deviceBreakdown.map((row) => (
									<li key={row.device_type} className="list-group-item px-0 d-flex justify-content-between">
										<span>{row.device_type}</span>
										<span className="fw-semibold">{row.events}</span>
									</li>
								))}
								{deviceBreakdown.length === 0 && (
									<li className="list-group-item px-0 text-muted small">No device data yet.</li>
								)}
							</ul>
						</div>
					</div>
				</div>
				<div className="col-12 col-md-6 col-xl-3">
					<div className="card h-100">
						<div className="card-header">Browser distribution</div>
						<div className="card-body">
							<ul className="list-group list-group-flush">
								{browserBreakdown.map((row) => (
									<li key={row.browser} className="list-group-item px-0 d-flex justify-content-between">
										<span>{row.browser}</span>
										<span className="fw-semibold">{row.events}</span>
									</li>
								))}
								{browserBreakdown.length === 0 && (
									<li className="list-group-item px-0 text-muted small">No browser data yet.</li>
								)}
							</ul>
						</div>
					</div>
				</div>
				<div className="col-12 col-md-6 col-xl-3">
					<div className="card h-100">
						<div className="card-header">Country (from IP)</div>
						<div className="card-body">
							<ul className="list-group list-group-flush">
								{countryBreakdown.map((row) => (
									<li key={row.country_code} className="list-group-item px-0 d-flex justify-content-between align-items-start gap-2">
										<span>{row.country_code}</span>
										<span className="text-end">
											<span className="fw-semibold d-block">{row.unique_visitors} visitors</span>
											<span className="text-muted small">{row.events} events</span>
										</span>
									</li>
								))}
								{countryBreakdown.length === 0 && (
									<li className="list-group-item px-0 text-muted small">
										No IP country data yet. New visits after deploy will appear here.
									</li>
								)}
							</ul>
						</div>
					</div>
				</div>
				<div className="col-12 col-md-6 col-xl-3">
					<div className="card h-100">
						<div className="card-header">Recent activity events</div>
						<div className="card-body" style={{ maxHeight: 320, overflowY: 'auto' }}>
							<ul className="list-group list-group-flush">
								{recentEvents.map((event, index) => (
									<li key={`${event.created_at}-${event.event_name}-${index}`} className="list-group-item px-0">
										<div className="fw-medium small">{event.event_name}</div>
										<div className="text-muted small text-break">{event.page_path || '(no page path)'}</div>
										<div className="text-muted small">
											{new Date(event.created_at).toLocaleString('en-GB')}
											{event.country_code ? ` · ${event.country_code}` : ''}
										</div>
									</li>
								))}
								{recentEvents.length === 0 && (
									<li className="list-group-item px-0 text-muted small">No recent events yet.</li>
								)}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}


